import { BreakdownDisplayMode } from "@redotech/crdb-enums/xborder-outbound/breakdown-display-mode";
import { LandedCostItem } from "@redotech/crdb-enums/xborder-outbound/landed-cost-item";
import { MarketPricingMode } from "@redotech/crdb-enums/xborder-outbound/market-pricing-mode";
import { MarkupStrategy } from "@redotech/crdb-enums/xborder-outbound/markup-strategy";
import { isRedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import { marketIdFormat } from "@redotech/redo-xborder-outbound-id";
import {
  COVERED_BUT_NOT_CHARGED_MESSAGE,
  deriveMarketPricingMode,
  INCLUDED_AND_EXCLUDED_MESSAGE,
  inclusionShapeError,
  marketDefinitionConflicts,
} from "@redotech/redo-xborder-outbound-inclusive-pricing/market-definition";
import * as z from "zod";

export const landedCostItem = z.enum(LandedCostItem);

// Fees are cart-level (per-shipment) and can never be baked into a per-item price.
export const priceableLandedCostItem = z.enum([
  LandedCostItem.Duties,
  LandedCostItem.Taxes,
]);

export const markupStrategy = z.enum(MarkupStrategy);

export const marketPricingMode = z.enum(MarketPricingMode);

export const breakdownDisplayMode = z.enum(BreakdownDisplayMode);

export const breakdownLabelOverrides = z.object({
  shipping: z.string().trim().min(1).max(80).optional(),
  taxes: z.string().trim().min(1).max(80).optional(),
  dutiesAndFees: z.string().trim().min(1).max(80).optional(),
});
export interface LandedCostBreakdownLabelOverrides extends z.infer<
  typeof breakdownLabelOverrides
> {}

export function refineMarketInclusion(
  value: {
    pricingMode?: MarketPricingMode | undefined;
    includeInPrice?: LandedCostItem[] | undefined;
    coverWithMarkup?: LandedCostItem[] | undefined;
    excludeInCheckout?: LandedCostItem[] | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  const { includeInPrice, pricingMode } = value;
  if (includeInPrice === undefined) {
    if (pricingMode === MarketPricingMode.AdaptiveCoefficient) {
      ctx.addIssue({
        code: "custom",
        message:
          "Adaptive coefficient requires includeInPrice: taxes alone, or " +
          "duties, taxes and fees together",
        path: ["includeInPrice"],
      });
    }
    return;
  }
  const shapeError = inclusionShapeError(
    includeInPrice,
    pricingMode ?? deriveMarketPricingMode(includeInPrice),
  );
  if (shapeError) {
    ctx.addIssue({
      code: "custom",
      message: shapeError,
      path: ["includeInPrice"],
    });
  }
  const conflicts = marketDefinitionConflicts(
    includeInPrice,
    value.coverWithMarkup ?? [],
    value.excludeInCheckout ?? [],
  );
  if (conflicts.coveredButNotCharged.length > 0) {
    ctx.addIssue({
      code: "custom",
      message: `${COVERED_BUT_NOT_CHARGED_MESSAGE}: ${conflicts.coveredButNotCharged.join(", ")}`,
      path: ["coverWithMarkup"],
    });
  }
  if (conflicts.includedAndExcluded.length > 0) {
    ctx.addIssue({
      code: "custom",
      message: `${INCLUDED_AND_EXCLUDED_MESSAGE}: ${conflicts.includedAndExcluded.join(", ")}`,
      path: ["excludeInCheckout"],
    });
  }
}

export const marketDefinitionWireFields = {
  pricingMode: marketPricingMode,
  markup: z.number(),
  markupStrategy,
  includeInPrice: z.array(landedCostItem),
  excludeInCheckout: z.array(landedCostItem),
  coverWithMarkup: z.array(landedCostItem),
  breakdownDisplayMode,
  breakdownLabelOverrides,
};

export const marketCountryCode = z
  .string()
  .regex(/^[A-Z]{2}$/, "Must be ISO 3166-1 alpha-2 uppercase")
  .refine(
    (code) => isRedoShopifyCountryCode(code),
    "Shopify does not support this country",
  );

export const marketSettingsInputFields = {
  marketId: marketIdFormat.schema.optional(),
  name: z.string().nullable().optional(),
  enabled: z.boolean().optional(),
  ...z.object(marketDefinitionWireFields).partial().shape,
};
